import React, { useState, useEffect } from 'react';
import { Alert, Severity, AlertType, AlertStatus } from './NOCDashboard';
import { NOC_API_ENDPOINTS, nocApiRequest } from '../config/api';

interface NOCAlertsListProps {
  showAll: boolean; // If false, show only recent alerts (dashboard view)
  onUserClick?: (userId: string) => void;
}

const NOCAlertsList: React.FC<NOCAlertsListProps> = ({ showAll, onUserClick }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<Severity | 'ALL'>('ALL');
  const [filterStatus, setFilterStatus] = useState<AlertStatus | 'ALL'>('ALL');

  // Fetch real alerts from API
  const fetchAlerts = async () => {
    try {
      let url = `${NOC_API_ENDPOINTS.alerts}?limit=50`;

      // Add filters if specified
      if (filterStatus !== 'ALL') {
        url += `&status=${filterStatus}`;
      }
      if (filterSeverity !== 'ALL') {
        url += `&severity=${filterSeverity}`;
      }

      const data = await nocApiRequest(url);

      if (data.alerts) {
        setAlerts(data.alerts);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  // Fetch alerts on mount and when filters change
  useEffect(() => {
    fetchAlerts();
    // Poll for new alerts every 10 seconds
    const interval = setInterval(fetchAlerts, 10000);
    return () => clearInterval(interval);
  }, [filterSeverity, filterStatus]);

  const filteredAlerts = alerts.filter(alert => {
    if (filterSeverity !== 'ALL' && alert.severity !== filterSeverity) return false;
    if (filterStatus !== 'ALL' && alert.status !== filterStatus) return false;
    return true;
  });

  const displayAlerts = showAll ? filteredAlerts : filteredAlerts.slice(0, 5);

  const getSeverityColor = (severity: Severity) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-900 text-red-300 border-red-700';
      case 'HIGH': return 'bg-orange-900 text-orange-300 border-orange-700';
      case 'MEDIUM': return 'bg-yellow-900 text-yellow-300 border-yellow-700';
      case 'LOW': return 'bg-blue-900 text-blue-300 border-blue-700';
    }
  };

  const getStatusColor = (status: AlertStatus) => {
    switch (status) {
      case 'OPEN': return 'text-red-400';
      case 'ACKNOWLEDGED': return 'text-yellow-400';
      case 'RESOLVED': return 'text-green-400';
    }
  };

  const getAlertTypeIcon = (type: AlertType) => {
    switch (type) {
      case 'RATE_ABUSE':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />;
      case 'GUARDRAIL_EVASION':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />;
      case 'ABUSIVE_CONTENT':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />;
      case 'ANOMALY':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />;
    }
  };

  const handleResolveAlert = async (alertId: string, actionTaken?: string) => {
    try {
      await nocApiRequest(NOC_API_ENDPOINTS.updateAlert(alertId), {
        method: 'PUT',
        body: JSON.stringify({
          status: 'RESOLVED',
          operator: 'operator_demo',
          action_taken: actionTaken || 'Manually resolved by operator'
        })
      });

      // Update local state
      setAlerts(prev => prev.map(alert =>
        alert.alert_id === alertId
          ? {
              ...alert,
              status: 'RESOLVED' as AlertStatus,
              resolved_by: 'operator_demo',
              action_taken: actionTaken || 'Manually resolved by operator'
            }
          : alert
      ));
      setSelectedAlert(null);

      // Refresh alerts
      await fetchAlerts();
    } catch (error) {
      console.error('Failed to resolve alert:', error);
      window.alert('Failed to resolve alert. Please try again.');
    }
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await nocApiRequest(NOC_API_ENDPOINTS.updateAlert(alertId), {
        method: 'PUT',
        body: JSON.stringify({
          status: 'ACKNOWLEDGED',
          operator: 'operator_demo'
        })
      });

      // Update local state
      setAlerts(prev => prev.map(alert =>
        alert.alert_id === alertId
          ? { ...alert, status: 'ACKNOWLEDGED' as AlertStatus }
          : alert
      ));

      // Refresh alerts
      await fetchAlerts();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      window.alert('Failed to acknowledge alert. Please try again.');
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-750 border-b border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-orange-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <h3 className="text-sm font-semibold text-white">
              {showAll ? 'All Alerts' : 'Recent Alerts'}
            </h3>
            <span className="ml-2 px-2 py-0.5 text-xs bg-orange-900 text-orange-300 rounded">
              {displayAlerts.length}
            </span>
          </div>
        </div>

        {/* Filters (only show in full view) */}
        {showAll && (
          <div className="flex items-center space-x-3">
            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value as Severity | 'ALL')}
              className="px-3 py-1.5 text-xs bg-gray-700 border border-gray-600 text-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
              <option value="ALL">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as AlertStatus | 'ALL')}
              className="px-3 py-1.5 text-xs bg-gray-700 border border-gray-600 text-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
              <option value="ALL">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="ACKNOWLEDGED">Acknowledged</option>
              <option value="RESOLVED">Resolved</option>
            </select>
          </div>
        )}
      </div>

      {/* Alerts List */}
      <div className={showAll ? 'max-h-[600px] overflow-y-auto' : 'max-h-96 overflow-y-auto'}>
        {displayAlerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500">
            <p className="text-sm">No alerts match filters</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {displayAlerts.map((alert) => (
              <div
                key={alert.alert_id}
                className="px-4 py-3 hover:bg-gray-750 transition-colors cursor-pointer"
                onClick={() => setSelectedAlert(alert)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {/* Alert Type Icon */}
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {getAlertTypeIcon(alert.alert_type)}
                      </svg>
                    </div>

                    {/* Alert Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs rounded border ${getSeverityColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                        <span className="text-xs text-gray-500">{alert.alert_type.replace(/_/g, ' ')}</span>
                        <span className={`text-xs ${getStatusColor(alert.status)}`}>● {alert.status}</span>
                      </div>
                      <p className="text-sm text-white mb-1">{alert.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-400">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onUserClick && onUserClick(alert.user_id);
                          }}
                          className="font-mono text-blue-400 hover:text-blue-300 hover:underline"
                        >
                          {alert.user_id}
                        </button>
                        <span>{alert.evidence_count} events</span>
                        <span>{new Date(alert.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  {alert.status === 'OPEN' && !showAll && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcknowledgeAlert(alert.alert_id);
                      }}
                      className="ml-4 px-2 py-1 text-xs bg-yellow-900/50 text-yellow-300 rounded hover:bg-yellow-900/70 transition-colors"
                    >
                      Acknowledge
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 text-sm rounded border ${getSeverityColor(selectedAlert.severity)}`}>
                  {selectedAlert.severity}
                </span>
                <h2 className="text-lg font-semibold text-white">Alert Details</h2>
              </div>
              <button
                onClick={() => setSelectedAlert(null)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="text-xs text-gray-400">Alert ID</label>
                <p className="text-sm text-white font-mono">{selectedAlert.alert_id}</p>
              </div>

              <div>
                <label className="text-xs text-gray-400">Type</label>
                <p className="text-sm text-white">{selectedAlert.alert_type.replace(/_/g, ' ')}</p>
              </div>

              <div>
                <label className="text-xs text-gray-400">User</label>
                <p className="text-sm text-white font-mono">{selectedAlert.user_id}</p>
              </div>

              <div>
                <label className="text-xs text-gray-400">Description</label>
                <p className="text-sm text-white">{selectedAlert.description}</p>
              </div>

              <div>
                <label className="text-xs text-gray-400">Recommendation</label>
                <p className="text-sm text-yellow-300">{selectedAlert.recommendation}</p>
              </div>

              <div>
                <label className="text-xs text-gray-400">Evidence</label>
                <p className="text-sm text-white">{selectedAlert.evidence_count} related events</p>
              </div>

              <div>
                <label className="text-xs text-gray-400">Timestamp</label>
                <p className="text-sm text-white">{new Date(selectedAlert.timestamp).toLocaleString()}</p>
              </div>

              {selectedAlert.status === 'RESOLVED' && (
                <div className="bg-green-900/30 border border-green-700 rounded p-3">
                  <p className="text-sm text-green-300">
                    <strong>Resolved by:</strong> {selectedAlert.resolved_by}
                  </p>
                  <p className="text-sm text-green-300 mt-1">
                    <strong>Action taken:</strong> {selectedAlert.action_taken}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            {selectedAlert.status !== 'RESOLVED' && (
              <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex justify-end space-x-3">
                {selectedAlert.status === 'OPEN' && (
                  <button
                    onClick={() => handleAcknowledgeAlert(selectedAlert.alert_id)}
                    className="px-4 py-2 bg-yellow-900 text-yellow-300 rounded hover:bg-yellow-800 transition-colors"
                  >
                    Acknowledge
                  </button>
                )}
                <button
                  onClick={() => handleResolveAlert(selectedAlert.alert_id)}
                  className="px-4 py-2 bg-green-900 text-green-300 rounded hover:bg-green-800 transition-colors"
                >
                  Resolve Alert
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NOCAlertsList;
